import ReferralFormLayout from './referral-form-layout'
import { type ISendReferral } from '@/application/send-referral/use-send-referral'
import { useTranslation } from 'react-i18next'
import { TextField } from '@mui/material'
import { useEffect, useState } from 'react'
import { ActionButton, toast } from '@itsrever/design-system'
import SendIcon from '@/assets/images/icons/SendIcon'
import MagicModal from '@/ui/components/magic-modal/magic-modal'
import { useAppSelector } from '@/redux/hooks'
import GiftIcon from '@/assets/images/icons/GiftIcon'

export default function ReferralByEmail({
  sendReferral,
  isLoading,
  success
}: {
  sendReferral: (params: ISendReferral) => void
  isLoading: boolean
  success: boolean
}) {
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [brand, setBrand] = useState('')
  const [website, setWebsite] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)

  const { t } = useTranslation()

  const referrer = useAppSelector((store) => store.userApi.getMe.response.user)

  const handleEmailReferral = () => {
    sendReferral({
      group: referrer?.group || '',
      referrerEmail: referrer?.name || 'marius@itsrever.com',
      referredEmail: email,
      referredPhone: phone,
      referredBrand: brand,
      referredWebsite: website
    })
  }

  const handleEmailReferralWithExtraData = () => {
    if (!phone && !brand) {
      toast({
        text: t('referrals.toast_messages.missing_info'),
        variant: 'error'
      })
      return
    }
    handleEmailReferral()
  }

  const handleStepOne = () => {
    if (!validateEmail(email)) {
      toast({
        text: t('referrals.toast_messages.missing_email'),
        variant: 'error'
      })
      return
    }
    setIsModalOpen(true)
  }

  const handleModalClose = () => {
    handleEmailReferral()
    setIsModalOpen(false)
  }

  useEffect(() => {
    if (success) {
      setIsModalOpen(false)
      setEmail('')
      setPhone('')
      setBrand('')
      setWebsite('')
    }
  }, [isLoading, success])

  return (
    <>
      <ReferralFormLayout>
        <div id="referralByEmail" className="w-fit">
          <div className="flex size-9 items-center justify-center rounded-[50%] bg-[#ebf1ff] text-white">
            <GiftIcon />
          </div>
          <h2 className="font-logo mb-1 mt-4 flex text-5xl font-light">
            {t('referrals.refer_by_email.title')}
          </h2>
          <p className="mb-3 w-fit max-w-[450px] font-light">
            {t('referrals.refer_by_email.description')}
          </p>
          <div className="flex gap-2">
            <TextField
              size="small"
              label="Email"
              placeholder="ceo@inditex.com"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
            />
            <ActionButton
              sx={{
                padding: '12px 1rem',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}
              onClick={handleStepOne}
            >
              <SendIcon />
              <p> {t('referrals.refer_by_email.actionText')}</p>
            </ActionButton>
          </div>
        </div>
      </ReferralFormLayout>
      <div className="relative">
        <MagicModal
          modalId="referralModalEmail"
          onCloseModal={handleModalClose}
          showModal={isModalOpen}
          targetId="referrals-hero"
          width={600}
          height={350}
        >
          <div className="flex flex-col items-center justify-center gap-3 p-5">
            <div className="text-center">
              <h3>{t('referrals.refer_by_email.modal.title')}</h3>
              <p className="mt-2 font-light">
                {t('referrals.refer_by_email.modal.subtitle')}
              </p>
            </div>
            <TextField
              size="small"
              required
              fullWidth
              label={t('referrals.refer_by_email.modal.brandNameInput')}
              placeholder="Inditex"
              value={brand}
              onChange={(e) => setBrand(e.currentTarget.value)}
            />
            <TextField
              size="small"
              required
              fullWidth
              label={t('referrals.refer_by_email.modal.websiteInput')}
              placeholder="zara.com"
              value={website}
              onChange={(e) => setWebsite(e.currentTarget.value)}
            />
            <TextField
              size="small"
              required
              fullWidth
              placeholder="+34 674 68 54 32"
              label={t('referrals.refer_by_email.modal.phoneInput')}
              value={phone}
              onChange={(e) => setPhone(e.currentTarget.value)}
            />
            <ActionButton
              sx={{
                padding: '12px 1rem',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                width: '100%',
                opacity: phone && brand ? '1' : '0.5'
              }}
              onClick={handleEmailReferralWithExtraData}
            >
              <SendIcon />
              <p> {t('referrals.refer_by_email.modal.actionText')}</p>
            </ActionButton>
            <span
              onClick={handleModalClose}
              className="cursor-pointer pt-1 text-xs text-grey-3"
            >
              {t('referrals.refer_by_email.modal.skip')}
            </span>
          </div>
        </MagicModal>
      </div>
    </>
  )
}

function validateEmail(email: string): boolean {
  const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
  return regex.test(email)
}
